import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, Camera, Clock, Car, Bell, MapPin } from 'lucide-react';
import { authFetch, API_BASE_URL, createPlacaInteresse } from "../lib/authFetch";
import { toast } from "sonner";
import { ocrConfidenceStyle, isAppleMobileUa } from "@/lib/ocrDisplay";

interface VehicleResult {
  id: number;
  plate: string;
  brand: string;
  model: string;
  color: string;
  year: string;
  /** Cor só da visão (HSV), quando a API envia */
  colorDetected: string;
  /** Carro / Moto / … detectado na câmera */
  vehicleCategoryPt: string;
  colorConfidencePct: number | null;
  lastSeen: string;
  location: string;
  cameraId: string;
  cameraNome: string;
  image: string | null;
  confidence: number;
  ocrConfiancaPct: number | null;
}

interface VehicleTrackingProps {
  initialPlate?: string;
  searchRequestId?: number;
}

/** Evita crash se data_hora vier inválido (mobile Safari é sensível a renders quebrados). */
function formatDetecaoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

/**
 * Quantas fotos em base64 pedir à API e quantas exibir (mesmo valor).
 * iOS: muito poucas; resto mobile: poucas; desktop: mais linhas com foto.
 */
function maxResultsWithImage(): number {
  if (typeof window === 'undefined') return 20;
  if (isAppleMobileUa()) return 2;
  if (typeof window.matchMedia !== 'function') return 20;
  try {
    return window.matchMedia('(max-width: 768px)').matches ? 3 : 20;
  } catch {
    return 20;
  }
}

/** Limita o total de cartões renderizados (evita branco em browsers mobile por memória/pintura). */
function maxResultsTotal(): number {
  if (typeof window === 'undefined') return 60;
  if (isAppleMobileUa()) return 8;
  if (typeof window.matchMedia !== 'function') return 60;
  try {
    return window.matchMedia('(max-width: 768px)').matches ? 10 : 60;
  } catch {
    return 60;
  }
}

function flushSetResults(
  gen: number,
  fetchGenRef: { current: number },
  results: VehicleResult[],
  setResults: (r: VehicleResult[]) => void,
): void {
  const apply = () => {
    if (gen !== fetchGenRef.current) return;
    setResults(results);
  };
  if (isAppleMobileUa() && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(apply);
    });
  } else {
    apply();
  }
}

/** Dedup pela API (mesmo segundo), antes do map — evita duplicata quando só mudam microsegundos no ISO. */
/** Mesma regra do backend: valor 0–1 vira %; já em 0–100 mantém. */
function ocrConfiancaParaPct(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const x = Number(raw);
  if (!Number.isFinite(x) || x < 0) return null;
  if (x <= 1) return Math.round(x * 1000) / 10;
  return Math.round(Math.min(x, 100) * 10) / 10;
}

function dedupeRawDeteccoes(list: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const item of list) {
    const rawTs = item.data_hora != null ? String(item.data_hora) : '';
    const tsKey = rawTs.length >= 19 ? rawTs.slice(0, 19) : rawTs;
    const fp = `${String(item.placa ?? '').toUpperCase()}|${item.camera_id ?? ''}|${tsKey}`;
    if (seen.has(fp)) continue;
    seen.add(fp);
    out.push(item);
  }
  return out;
}

function safeText(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

const VehicleTracking = ({ initialPlate, searchRequestId }: VehicleTrackingProps) => {
  const fetchGen = useRef(0);
  const inFlightPlateRef = useRef<string>('');
  const lastAutoTriggerKeyRef = useRef<string>('');
  const activeAbortRef = useRef<AbortController | null>(null);
  const [searchPlate, setSearchPlate] = useState(initialPlate || '');
  const [searchResults, setSearchResults] = useState<VehicleResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchedPlate, setLastSearchedPlate] = useState('');
  // Indicadores
  const [indicators, setIndicators] = useState<{ consultas_hoje: number; veiculos_encontrados: number; taxa_sucesso: number } | null>(null);
  // Função para buscar indicadores (pode ser chamada em vários momentos)
  const fetchIndicators = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/veiculos/indicadores`);
      if (response.ok) {
        const data = await response.json();
        setIndicators(data);
      }
    } catch (e) { setIndicators(null); }
  };

  useEffect(() => {
    // Buscar indicadores ao montar o componente
    fetchIndicators();
    return () => {
      activeAbortRef.current?.abort();
      activeAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (initialPlate && initialPlate.trim()) {
      const normalized = initialPlate.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
      if (!normalized) return;
      const triggerKey = `${searchRequestId ?? 'na'}:${normalized}`;
      if (triggerKey === lastAutoTriggerKeyRef.current) return;
      lastAutoTriggerKeyRef.current = triggerKey;
      setSearchPlate(normalized);
      handleSearch(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRequestId]);

  const handleSearch = async (plateOverride?: string) => {
    const plate = (plateOverride || searchPlate).replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
    if (!plate.trim()) return;
    if (isSearching && inFlightPlateRef.current === plate) return;
    activeAbortRef.current?.abort();
    const controller = new AbortController();
    activeAbortRef.current = controller;
    const gen = ++fetchGen.current;
    inFlightPlateRef.current = plate;
    setIsSearching(true);
    setHasSearched(true);
    setLastSearchedPlate(plate);
    const maxImg = maxResultsWithImage();
    const limitRows = maxResultsTotal();
    try {
      const plateParam = encodeURIComponent(plate);
      await authFetch(`${API_BASE_URL}/veiculos/busca-rapida?placa=${plateParam}`, {
        signal: controller.signal,
      });
      if (gen !== fetchGen.current) return;

      const response = await authFetch(
        `${API_BASE_URL}/detecoes_camera?placa=${plateParam}&limite=${limitRows}&max_fotos=${maxImg}`,
        { signal: controller.signal }
      );

      if (gen !== fetchGen.current) return;

      if (response.ok) {
        let raw: unknown;
        try {
          raw = await response.json();
        } catch {
          if (gen === fetchGen.current) {
            toast.error('Resposta inválida do servidor. Tente novamente.');
            setSearchResults([]);
          }
          return;
        }
        const list = dedupeRawDeteccoes(Array.isArray(raw) ? raw : []).slice(0, limitRows);
        const mappedResults: VehicleResult[] = [];
        list.forEach((item: any, idx: number) => {
          try {
            const nid = item.id;
            const idNum = typeof nid === 'number' ? nid : Number(nid);
            const b64 = item.imagem_base64;
            const dataUrl =
              typeof b64 === 'string' && b64.length > 0 && idx < maxImg
                ? `data:image/jpeg;base64,${b64}`
                : null;
            const tipoPt = safeText(item.tipo_veiculo_pt, '').trim();
            const corDet = safeText(item.cor_detectada, '').trim();
            const corMerged =
              corDet ||
              safeText(item.cor, '').trim();
            mappedResults.push({
              id: Number.isFinite(idNum) ? idNum : idx,
              plate: safeText(item.placa, '').toUpperCase(),
              brand: '',
              model: safeText(item.modelo, ''),
              color: corMerged,
              colorDetected: corDet,
              vehicleCategoryPt: tipoPt,
              colorConfidencePct: ocrConfiancaParaPct(item.veiculo_cor_confianca),
              year: safeText(item.ano, ''),
              lastSeen: formatDetecaoDate(item.data_hora),
              location: safeText(item.localizacao, '').trim() || 'Localização não informada',
              cameraId: item.camera_id ? `Câmera ${safeText(item.camera_id, '')}` : '',
              cameraNome: safeText(item.camera_nome, ''),
              image: dataUrl,
              confidence: ocrConfiancaParaPct(item.ocr_confianca) ?? 0,
              ocrConfiancaPct: ocrConfiancaParaPct(item.ocr_confianca),
            });
          } catch {
            /* ignora linha corrompida */
          }
        });
        if (gen !== fetchGen.current) return;
        flushSetResults(gen, fetchGen, mappedResults, setSearchResults);
        fetchIndicators();
      } else {
        setSearchResults([]);
        fetchIndicators();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('VehicleTracking busca:', error);
      if (gen === fetchGen.current) {
        toast.error('Não foi possível concluir a busca. Tente novamente.');
        setSearchResults([]);
        fetchIndicators();
      }
    } finally {
      if (gen === fetchGen.current) {
        setIsSearching(false);
        inFlightPlateRef.current = '';
        if (activeAbortRef.current === controller) {
          activeAbortRef.current = null;
        }
      }
    }
  };

  const showResultSkeleton = isSearching && hasSearched && searchResults.length === 0;
  const displayResults = searchResults.map((r, idx) => ({
    ...r,
    id: Number.isFinite(Number(r.id)) ? Number(r.id) : idx,
    plate: safeText(r.plate, '').toUpperCase(),
    model: safeText(r.model, ''),
    color: safeText(r.color, ''),
    colorDetected: safeText(r.colorDetected, ''),
    vehicleCategoryPt: safeText(r.vehicleCategoryPt, ''),
    year: safeText(r.year, ''),
    lastSeen: safeText(r.lastSeen, '—'),
    location: safeText(r.location, 'Localização não informada'),
    cameraId: safeText(r.cameraId, ''),
    cameraNome: safeText(r.cameraNome, ''),
  }));

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card className="rounded-xl border border-protec-gold/25 bg-gradient-to-br from-protec-dark-light to-zinc-950/80 shadow-lg shadow-black/25">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg tracking-tight">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-protec-gold/15 ring-1 ring-protec-gold/30">
              <Search className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            </span>
            Rastreamento de Veículos
          </CardTitle>
          <CardDescription className="text-gray-400 text-xs sm:text-sm">
            Consulta instantânea nas detecções das câmeras — placas Mercosul ou antigas (até 7 caracteres)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="plate" className="text-gray-300 text-sm">Placa do Veículo</Label>
              <Input
                id="plate"
                value={searchPlate}
                onChange={(e) => {
                  // Remove caracteres especiais e limita a 7 dígitos
                  const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
                  setSearchPlate(value);
                }}
                placeholder="ABC1234"
                className="bg-protec-dark/90 border-protec-gold/30 text-white mt-1 font-mono text-lg tracking-widest placeholder:text-zinc-600 focus-visible:ring-protec-gold/50"
                maxLength={7}
              />
            </div>
            <div>
              <Label htmlFor="brand" className="text-gray-300 text-sm">Marca (Opcional)</Label>
              <Input
                id="brand"
                placeholder="Toyota, Ford..."
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="color" className="text-gray-300 text-sm">Cor (Opcional)</Label>
              <Input
                id="color"
                placeholder="Branco, Preto..."
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button 
              onClick={() => handleSearch()}
              disabled={isSearching || !searchPlate.trim()}
              className="bg-protec-gold text-black hover:shadow-gold w-full sm:w-auto"
            >
              {isSearching ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Buscando...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Iniciar Rastreamento</span>
                  <span className="sm:hidden">Buscar</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {showResultSkeleton && (
        <Card className="rounded-xl border border-protec-gold/20 bg-protec-dark-light/90 overflow-hidden">
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="h-4 w-48 rounded bg-zinc-700/60 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="aspect-video rounded-xl bg-zinc-800/80 animate-pulse ring-1 ring-white/5" />
              <div className="md:col-span-2 space-y-3">
                <div className="h-8 w-40 rounded-lg bg-zinc-700/50 animate-pulse" />
                <div className="h-4 w-full max-w-md rounded bg-zinc-800/60 animate-pulse" />
                <div className="h-4 w-56 max-w-[75%] rounded bg-zinc-800/40 animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats - sempre visível, agora abaixo do bloco de consulta */}
      {indicators && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card className="bg-protec-dark-light border-protec-gold/20">
            <CardContent className="p-2 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Consultas</p>
                  <p className="text-lg sm:text-xl font-bold text-white">{Number(indicators.consultas_hoje) || 0}</p>
                </div>
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold hidden sm:block" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-protec-dark-light border-protec-gold/20">
            <CardContent className="p-2 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Encontrados</p>
                  <p className="text-lg sm:text-xl font-bold text-white">{Number(indicators.veiculos_encontrados) || 0}</p>
                </div>
                <Car className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold hidden sm:block" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-protec-dark-light border-protec-gold/20">
            <CardContent className="p-2 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1 sm:gap-0 text-center sm:text-left">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Sucesso</p>
                  <p className="text-lg sm:text-xl font-bold text-white">
                    {typeof indicators.taxa_sucesso === 'number' && !Number.isNaN(indicators.taxa_sucesso)
                      ? `${indicators.taxa_sucesso}%`
                      : '—'}
                  </p>
                </div>
                <Badge className="bg-green-500/20 text-green-400 text-xs hidden sm:flex">
                  {(Number(indicators.taxa_sucesso) || 0) >= 80
                    ? 'Alto'
                    : (Number(indicators.taxa_sucesso) || 0) >= 50
                      ? 'Médio'
                      : 'Baixo'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Mensagem de nenhum resultado encontrado */}
      {hasSearched && displayResults.length === 0 && !isSearching && (
        <Card className="rounded-xl border border-zinc-700/50 bg-zinc-950/40">
          <CardContent className="py-10 sm:py-14 text-center px-4">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-800/80 ring-1 ring-protec-gold/20">
              <Search className="w-8 h-8 text-zinc-500" />
            </div>
            <h3 className="text-white text-base sm:text-lg font-semibold mb-2">Nenhuma detecção encontrada</h3>
            <p className="text-gray-400 text-xs sm:text-sm max-w-md mx-auto leading-relaxed">
              Não há registros para{' '}
              <span className="font-mono tracking-widest text-protec-gold">{lastSearchedPlate}</span> no período consultado nas câmeras vinculadas.
            </p>
            <p className="text-gray-500 text-xs mt-3">
              Confira os caracteres (Mercosul: letras e números misturados) ou tente mais tarde.
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Results Section */}
      {displayResults.length > 0 && (
        <Card className="rounded-xl border border-protec-gold/20 bg-gradient-to-b from-protec-dark-light to-zinc-950/90 shadow-xl shadow-black/30">
          <CardHeader className="pb-2 sm:pb-4 border-b border-protec-gold/10">
            <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/25">
                <Car className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400/90" />
              </span>
              Detecções ({displayResults.length})
            </CardTitle>
            <CardDescription className="text-gray-400 text-xs sm:text-sm">
              Placa <span className="font-mono text-protec-gold/90 tracking-widest">{lastSearchedPlate}</span> — ordenadas por data da captura
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4 sm:pt-6">
            <div className="space-y-5">
              {displayResults.map((result, idx) => (
                <div
                  key={`${result.id}-${result.lastSeen}-${idx}`}
                  className="group relative overflow-hidden rounded-xl border border-white/5 bg-zinc-900/40 p-3 sm:p-5 shadow-md shadow-black/20 ring-1 ring-protec-gold/5 transition hover:border-protec-gold/20 hover:ring-protec-gold/15"
                >
                  <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-protec-gold/80 to-protec-gold/20 opacity-80" aria-hidden />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 pl-2">
                    {/* Foto do veículo na passagem */}
                    <div className="relative">
                      <div className="aspect-video min-h-[120px] overflow-hidden rounded-xl bg-zinc-950 ring-1 ring-white/10 shadow-inner">
                        {result.image ? (
                          <img
                            src={result.image}
                            alt={`Veículo ${result.plate}`}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                            loading={isAppleMobileUa() ? 'eager' : 'lazy'}
                            decoding="async"
                          />
                        ) : (
                          <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 text-zinc-600">
                            <Camera className="w-8 h-8 opacity-50" />
                            <span className="text-xs">Sem miniatura</span>
                          </div>
                        )}
                      </div>
                      <Badge className="absolute bottom-2 left-2 border-0 bg-black/75 text-[10px] text-zinc-200 backdrop-blur-sm sm:text-xs">
                        {result.lastSeen}
                      </Badge>
                    </div>
                    {/* Details: horário, local e câmera ao lado da foto */}
                    <div className="md:col-span-2 space-y-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <h3 className="font-mono text-xl sm:text-2xl font-semibold tracking-[0.18em] text-white sm:tracking-[0.22em]">
                          {result.plate}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          {(() => {
                            const o = ocrConfidenceStyle(result.ocrConfiancaPct);
                            return (
                              <Badge variant="outline" className={`text-[10px] sm:text-xs font-medium ${o.className}`}>
                                {result.ocrConfiancaPct != null ? `OCR ${Math.round(result.ocrConfiancaPct)}%` : 'OCR —'}
                              </Badge>
                            );
                          })()}
                          <Badge className="border-0 bg-emerald-500/15 text-emerald-300/95 text-[10px] sm:text-xs ring-1 ring-emerald-500/25">
                            Registrado
                          </Badge>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed border-l-2 border-protec-gold/35 pl-3">
                        {(result.cameraNome || result.cameraId) && (
                          <span className="text-white font-medium block">
                            {result.cameraNome || result.cameraId}
                          </span>
                        )}
                        <span className="text-gray-400 block mt-1">
                          {result.location}
                        </span>
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <p className="text-gray-500 uppercase tracking-wide text-[10px] sm:text-xs">Categoria</p>
                          <p className="text-white font-medium truncate">
                            {result.vehicleCategoryPt || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 uppercase tracking-wide text-[10px] sm:text-xs">Cor (visão)</p>
                          <p className="text-white font-medium truncate">
                            {result.colorDetected || result.color || "—"}
                          </p>
                          {result.colorConfidencePct != null && result.colorDetected && (
                            <p className="text-gray-500 text-[10px] mt-0.5 tabular-nums">
                              {result.colorConfidencePct}%
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-500 uppercase tracking-wide text-[10px] sm:text-xs">Modelo (cadastro)</p>
                          <p className="text-white truncate" title="Preenchido se a placa existir na base de veículos">
                            {result.model || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 uppercase tracking-wide text-[10px] sm:text-xs">Ano (cadastro)</p>
                          <p className="text-white truncate">{result.year || "—"}</p>
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <div className="flex items-center gap-2 text-xs sm:text-sm">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-protec-gold flex-shrink-0" />
                          <span className="text-gray-400">Data e hora:</span>
                          <span className="text-white font-medium">{result.lastSeen}</span>
                        </div>
                        {result.cameraId ? (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Camera className="w-3 h-3 flex-shrink-0" />
                            <span>ID {result.cameraId}</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1 sm:pt-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="rounded-lg bg-protec-gold/10 text-protec-gold hover:bg-protec-gold/20 border border-protec-gold/30 text-xs h-7 sm:h-8 px-2 sm:px-3"
                          onClick={() => {
                            const endereco = encodeURIComponent(result.location || '');
                            window.open(`https://www.google.com/maps/search/?api=1&query=${endereco}`, '_blank');
                          }}
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          <span className="hidden xs:inline">Ver no </span>Mapa
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="rounded-lg bg-protec-gold/10 text-protec-gold hover:bg-protec-gold/20 border border-protec-gold/30 text-xs h-7 sm:h-8 px-2 sm:px-3"
                          onClick={async () => {
                            try {
                              await createPlacaInteresse({
                                placa: result.plate,
                                descricao: `Alerta criado via busca - ${result.model || 'Veículo'} ${result.color || ''}`.trim(),
                                ativo: true
                              });
                              toast.success(`Alerta criado para a placa ${result.plate}!`);
                            } catch (error) {
                              toast.error("Erro ao criar alerta. Tente novamente.");
                            }
                          }}
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          Alerta
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default VehicleTracking;
